import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useApolloClient } from '@apollo/client/react';
import { getLoggedInAs } from '@realadvisor/auth-client';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

import { useViewer } from '../audience/useViewer';
import { getCampaignsForTour } from '../campaigns/campaignRegistry';
import { evaluateCampaign } from '../campaigns/evaluateCampaign';
import type { DismissReason } from '../state/experienceState';
import { useProductExperiences } from '../state/ProductExperiencesProvider';

import { TourOverlay } from './TourOverlay';
import { type TourTrigger, planOpenTour, resolveTourSteps } from './tourPolicy';
import {
  type TourId,
  getTourDefinition,
  hasTourPath,
  resolveTourPath,
} from './tourRegistry';
import type { TourStep } from './tourTypes';

// QA: `?tour_preview=<id>` starts that tour on its screen even when it was
// already completed, and records nothing.
export const TOUR_PREVIEW_PARAM = 'tour_preview';

// Router state carried by "Show me" links (`useOpenTour`), read once by the
// target screen's `useAutoTour`.
const TOUR_START_STATE = 'productTour';
type TourStartState = { id: string; preview: boolean };

const SESSION_KEY = 'ra_auto_tour_shown';
// Data-driven screens render their anchors after a query resolves.
const ANCHOR_WAIT_MS = 8000;
const ANCHOR_POLL_MS = 250;
// After a reveal (a panel opening), how long the element may take to appear,
// and how long it must stay still before the step points at it.
const REVEAL_WAIT_MS = 1500;
const REVEAL_SETTLE_MS = 120;

const isShown = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return false;
  }
  if (element.checkVisibility?.({ visibilityProperty: true }) === false) {
    return false;
  }
  // A collapsed section (MUI `Collapse`) keeps its content laid out inside a
  // zero-height, clipped wrapper.
  for (
    let parent = element.parentElement;
    parent != null;
    parent = parent.parentElement
  ) {
    if (
      (parent.clientHeight === 0 || parent.clientWidth === 0) &&
      getComputedStyle(parent).overflow !== 'visible'
    ) {
      return false;
    }
  }
  return true;
};

const findAnchor = (anchor: string): HTMLElement | null => {
  const element = document.querySelector<HTMLElement>(
    `[data-tour="${CSS.escape(anchor)}"]`,
  );
  return element != null && isShown(element) ? element : null;
};

// Resolves once the anchor is on screen and has stopped moving (a panel
// finished opening), or with whatever is there after `REVEAL_WAIT_MS`.
const waitForSettledAnchor = (anchor: string): Promise<HTMLElement | null> =>
  new Promise(resolve => {
    const startedAt = performance.now();
    let last: { key: string; since: number } | null = null;
    const check = () => {
      const now = performance.now();
      const element = findAnchor(anchor);
      if (element != null) {
        const { top, left, width, height } = element.getBoundingClientRect();
        const key = `${top}:${left}:${width}:${height}`;
        if (last?.key !== key) {
          last = { key, since: now };
        } else if (now - last.since >= REVEAL_SETTLE_MS) {
          resolve(element);
          return;
        }
      } else {
        last = null;
      }
      if (now - startedAt >= REVEAL_WAIT_MS) {
        resolve(element);
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });

// Attention budget: at most one unprompted tour per browser tab session (sessionStorage).
export const autoTourSession = {
  isUsed: (): boolean => {
    try {
      return sessionStorage.getItem(SESSION_KEY) != null;
    } catch {
      return false;
    }
  },
  markUsed: () => {
    try {
      sessionStorage.setItem(SESSION_KEY, new Date().toISOString());
    } catch {
      // ignore
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
  },
};

type CampaignChoice = { campaignId: string; holdout: boolean };

type ActiveTour = {
  id: TourId;
  campaignId?: string;
  steps: TourStep[];
  index: number;
  // Which way the user is going: a step that can't be shown is skipped in
  // that direction.
  direction: 1 | -1;
  isPreview: boolean;
  startedAt: string;
};

type Reveal = { current: () => void };

type TourContextValue = {
  // Starts the tour once its first anchor is on screen: `auto` asks the
  // campaigns targeting it, the other triggers always play. Returns a cleanup
  // that cancels a pending start.
  request: (id: TourId, trigger: TourTrigger) => () => void;
  // See `useTourReveal`. Returns the cleanup.
  registerReveal: (anchors: readonly string[], reveal: Reveal) => () => void;
  // A `useAutoTour` that can play this tour is mounted (see `planOpenTour`).
  // `registerHost` returns the cleanup.
  registerHost: (id: TourId) => () => void;
  isHostMounted: (id: TourId) => boolean;
};

const TourContext = createContext<TourContextValue | null>(null);

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  const { me } = useAppData();
  const { t } = useLocale();
  const client = useApolloClient();
  const viewer = useViewer();
  const experiences = useProductExperiences();
  const [active, setActive] = useState<ActiveTour | null>(null);
  const userId = me?.id;
  // How to bring each anchor on screen (open its panel…), by anchor.
  const revealsRef = useRef(new Map<string, Reveal>());
  // Mounted `useAutoTour` hosts, by tour.
  const hostsRef = useRef(new Map<TourId, number>());

  const registerHost = useCallback((id: TourId) => {
    hostsRef.current.set(id, (hostsRef.current.get(id) ?? 0) + 1);
    return () => {
      const count = (hostsRef.current.get(id) ?? 1) - 1;
      if (count > 0) {
        hostsRef.current.set(id, count);
      } else {
        hostsRef.current.delete(id);
      }
    };
  }, []);

  const isHostMounted = useCallback(
    (id: TourId) => hostsRef.current.has(id),
    [],
  );

  const registerReveal = useCallback(
    (anchors: readonly string[], reveal: Reveal) => {
      for (const anchor of anchors) {
        revealsRef.current.set(anchor, reveal);
      }
      return () => {
        for (const anchor of anchors) {
          if (revealsRef.current.get(anchor) === reveal) {
            revealsRef.current.delete(anchor);
          }
        }
      };
    },
    [],
  );

  // A step's anchor, after running its reveal if the screen registered one.
  const showAnchor = useCallback(async (anchor: string) => {
    const reveal = revealsRef.current.get(anchor);
    if (reveal == null) {
      return findAnchor(anchor);
    }
    reveal.current();
    return waitForSettledAnchor(anchor);
  }, []);

  // Requests poll and await; they read the latest values through refs.
  const experiencesRef = useRef(experiences);
  experiencesRef.current = experiences;
  const activeRef = useRef(active);
  activeRef.current = active;

  // Which campaign, if any, plays this tour for this user right now.
  const pickCampaign = useCallback(
    async (tourId: TourId): Promise<CampaignChoice | null> => {
      if (userId == null || viewer == null) {
        return null;
      }
      for (const [campaignId, campaign] of getCampaignsForTour(tourId)) {
        const decision = await evaluateCampaign({
          id: campaignId,
          campaign,
          context: { client, userId, viewer },
          state: experiencesRef.current.getState(),
          autoTourShownThisSession: autoTourSession.isUsed(),
          isImpersonating: getLoggedInAs() != null,
        });
        if (decision.action === 'holdout') {
          return { campaignId, holdout: true };
        }
        if (decision.action === 'show') {
          return { campaignId, holdout: false };
        }
      }
      return null;
    },
    [client, userId, viewer],
  );

  const tRef = useRef(t);
  tRef.current = t;
  const pickCampaignRef = useRef(pickCampaign);
  pickCampaignRef.current = pickCampaign;

  const request = useCallback(
    (id: TourId, trigger: TourTrigger) => {
      if (userId == null) {
        return () => {};
      }
      const definition = getTourDefinition(id, tRef.current);
      const startAnchors = definition.startOnAnyStep
        ? definition.steps.map(step => step.anchor)
        : [definition.steps[0].anchor];
      const startedAt = Date.now();
      let timer: ReturnType<typeof setTimeout> | undefined;
      let cancelled = false;

      const start = (choice?: CampaignChoice) => {
        if (cancelled || activeRef.current != null) {
          return;
        }
        const steps = resolveTourSteps(
          getTourDefinition(id, tRef.current).steps,
          anchor =>
            findAnchor(anchor) != null || revealsRef.current.has(anchor),
        );
        if (steps.length === 0) {
          return;
        }
        if (choice?.holdout === true) {
          experiencesRef.current.recordCampaignExposure({
            campaignId: choice.campaignId,
            tourId: id,
            holdout: true,
          });
          return;
        }
        const campaignId = choice?.campaignId;
        const isPreview = trigger === 'preview';
        if (campaignId != null) {
          autoTourSession.markUsed();
          experiencesRef.current.recordCampaignExposure({
            campaignId,
            tourId: id,
            holdout: false,
          });
        }
        const next = {
          id,
          campaignId,
          steps,
          index: 0,
          direction: 1 as const,
          isPreview,
          startedAt: new Date().toISOString(),
        };
        activeRef.current = next;
        setActive(next);
        experiencesRef.current.recordTourStarted({
          tourId: id,
          campaignId,
          stepCount: steps.length,
          trigger,
          preview: isPreview,
        });
      };

      const attempt = () => {
        if (cancelled) {
          return;
        }
        const anchorReady = startAnchors.some(
          anchor => findAnchor(anchor) != null,
        );
        // Campaigns read the stored state: wait for it.
        const stateReady =
          trigger !== 'auto' || experiencesRef.current.stateLoaded;
        if (!anchorReady || !stateReady) {
          if (Date.now() - startedAt < ANCHOR_WAIT_MS) {
            timer = setTimeout(attempt, ANCHOR_POLL_MS);
          }
          return;
        }
        if (trigger !== 'auto') {
          start();
          return;
        }
        pickCampaignRef
          .current(id)
          .then(choice => {
            if (choice != null) {
              start(choice);
            }
          })
          .catch(campaignError => {
            console.warn(`Tour ${id}: campaign decision failed`, campaignError);
          });
      };

      timer = setTimeout(attempt, ANCHOR_POLL_MS);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    },
    [userId],
  );

  // One event per step shown.
  const activeStep = active?.steps[active.index];
  useEffect(() => {
    if (active != null && activeStep != null) {
      experiencesRef.current.trackTourStep({
        tourId: active.id,
        campaignId: active.campaignId,
        step: active.index + 1,
        stepCount: active.steps.length,
        anchor: activeStep.anchor,
        preview: active.isPreview,
      });
    }
    // Keyed on the step, not on the object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, active?.index]);

  const finish = useCallback(
    (outcome: 'completed' | 'dismissed', reason?: DismissReason) => {
      const current = activeRef.current;
      if (current == null) {
        return;
      }
      activeRef.current = null;
      setActive(null);
      experiencesRef.current.recordTourEnded({
        tourId: current.id,
        campaignId: current.campaignId,
        step: current.index + 1,
        stepCount: current.steps.length,
        outcome,
        reason,
        preview: current.isPreview,
        startedAt: current.startedAt,
      });
    },
    [],
  );

  const goTo = useCallback(
    (direction: 1 | -1) => {
      const current = activeRef.current;
      if (current == null) {
        return;
      }
      const index = current.index + direction;
      if (index >= current.steps.length) {
        finish('completed');
        return;
      }
      const next = { ...current, index: Math.max(0, index), direction };
      activeRef.current = next;
      setActive(next);
    },
    [finish],
  );

  // The step's element is gone (the user navigated) or never appeared.
  const skipStep = useCallback(() => {
    const current = activeRef.current;
    if (current == null) {
      return;
    }
    if (current.direction === -1 && current.index > 0) {
      goTo(-1);
    } else if (current.index + 1 < current.steps.length) {
      goTo(1);
    } else {
      finish('dismissed', 'navigated_away');
    }
  }, [goTo, finish]);

  const value = useMemo(
    () => ({ request, registerReveal, registerHost, isHostMounted }),
    [request, registerReveal, registerHost, isHostMounted],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {active != null && activeStep != null && (
        <TourOverlay
          key={`${active.id}:${active.index}`}
          step={activeStep}
          index={active.index}
          count={active.steps.length}
          showAnchor={showAnchor}
          onBack={() => goTo(-1)}
          onNext={() => goTo(1)}
          onUnavailable={skipStep}
          onDismiss={reason => finish('dismissed', reason)}
        />
      )}
    </TourContext.Provider>
  );
};

const useTourContext = (): TourContextValue => {
  const value = useContext(TourContext);
  if (value == null) {
    throw new Error('Tours need a TourProvider (ProductUpdatesProvider)');
  }
  return value;
};

// Call from the screen a tour explains: "this tour can play here". It plays
// when a campaign targets it for this user, or right away when the user got
// here from "Show me".
export const useAutoTour = (id: TourId, enabled = true, autoPlay = true) => {
  const { request, registerHost } = useTourContext();
  const location = useLocation();
  const navigate = useNavigate();
  // Read once: consuming the router state below re-renders this screen.
  const startState = useRef(
    (location.state as Record<string, TourStartState | undefined> | null)?.[
      TOUR_START_STATE
    ],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const requested = startState.current;
    if (requested?.id === id) {
      startState.current = undefined;
      // Back/forward must not replay it.
      navigate(location, { replace: true, state: null });
      return request(id, requested.preview ? 'preview' : 'explicit');
    }
    const preview =
      new URLSearchParams(location.search).get(TOUR_PREVIEW_PARAM) === id;
    if (!preview && !autoPlay) {
      return;
    }
    return request(id, preview ? 'preview' : 'auto');
    // `location` changes when the state is consumed; that must not restart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, enabled, autoPlay, request]);

  // Lets "Show me" start this tour while this screen stays mounted.
  useEffect(
    () => (enabled ? registerHost(id) : undefined),
    [id, enabled, registerHost],
  );
};

// For a step whose element sits in a closed panel, menu or tab: call in the
// component that owns that state, with the anchors it hides and how to show
// them. Before such a step, the tour runs `reveal` and waits for the element
// to settle; the panel stays open afterwards, so the user keeps the context.
export const useTourReveal = (
  anchors: string | readonly string[],
  reveal: () => void,
  enabled = true,
) => {
  // Optional: shared components also render outside the app shell (tests).
  const context = useContext(TourContext);
  const revealRef = useRef(reveal);
  revealRef.current = reveal;
  const key = typeof anchors === 'string' ? anchors : anchors.join('\n');
  const registerReveal = context?.registerReveal;

  useEffect(() => {
    if (!enabled || registerReveal == null) {
      return;
    }
    return registerReveal(key.split('\n'), revealRef);
  }, [key, enabled, registerReveal]);
};

// Behaviour triggers: call after something happened (a lead qualified, a
// first listing published…). Same rules as `useAutoTour`: the tour plays only
// if a campaign targets it, on the current screen.
export const useTourTrigger = () => {
  const { request } = useTourContext();
  return useCallback((id: TourId) => request(id, 'auto'), [request]);
};

// "Show me": open the screen a tour runs on for this user (their latest
// listing, …) and start the tour there. False when there is none (no listing
// they can see…): the caller says so rather than opening a page where the
// tour cannot play. `fallbackPath` is for tours without a screen of their
// own.
export const useOpenTour = () => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { request, isHostMounted } = useTourContext();
  const { me } = useAppData();
  const userId = me?.id;

  return useCallback(
    async (
      id: TourId,
      {
        fallbackPath,
        preview = false,
      }: { fallbackPath?: string; preview?: boolean },
    ): Promise<boolean> => {
      const target = !hasTourPath(id)
        ? fallbackPath
        : userId == null
        ? null
        : await resolveTourPath(id, { client, userId });
      if (target == null) {
        return false;
      }
      const plan = planOpenTour({
        target,
        current: `${location.pathname}${location.search}`,
        hostMounted: isHostMounted(id),
      });
      if (plan.kind === 'start') {
        if (plan.path != null) {
          navigate(plan.path);
        }
        // Waits for the tour's first anchor, on the new page too.
        request(id, preview ? 'preview' : 'explicit');
        return true;
      }
      const state: Record<string, TourStartState> = {
        [TOUR_START_STATE]: { id, preview },
      };
      navigate(plan.path, { state });
      return true;
    },
    [client, navigate, location, request, isHostMounted, userId],
  );
};
